import { Controller } from "@hotwired/stimulus"

declare global {
  interface Window {
    umami?: {
      track: (event: string, data?: Record<string, unknown>) => void;
    };
  }
}

// Discrete release scrubber for the arena history page.
//
// The rail spans [minDate, maxDate] and selection is CLICK-ONLY: click a
// release dot (or right next to one) to travel there. The lime marker just
// shows where you are; it isn't draggable.
//
// A MINIMAP brush above the rail (like a stock-chart range selector) sets the
// "display window" [dispMin, dispMax]:
//
//   • drag the brush's left/right grip → resize the window (zoom)
//   • drag the brush body               → pan the window
//
// The main rail renders only that window. Every in-window release shows its
// name: labels alternate above/below the line and STACK into as many vertical
// levels as the local density needs, so nothing is ever hidden behind a bare
// leader line. The controller owns all positioning and sizes the stage to fit.
//
// Committing a date hot-swaps the scrubber turbo-frame (frame mode) or visits
// the page (fallback), mirroring the date into the URL. The brush window is
// persisted per-mode so a pick doesn't snap the zoom back to full range.

const DAY = 86_400_000
type DragMode = "left" | "right" | "body" | "pan" | null

interface Point {
  idx: number // chronological index — fixes a point's above/below side
  time: number
  iso: string
  model: string
  date: string
  dot: HTMLElement
  conn: HTMLElement
  label: HTMLElement
}

interface Slot {
  above: boolean
  level: number
}

export default class extends Controller<HTMLElement> {
  static targets = ["stage", "handle", "tick", "dot", "conn", "label", "mini", "brush", "maskL", "maskR"]
  static values = {
    min: String,
    max: String,
    selected: String,
    typeParam: String,
    basePath: String,
    frameId: String,
    frameSrc: String
  }

  declare readonly stageTarget: HTMLElement
  declare readonly handleTarget: HTMLElement
  declare readonly tickTargets: HTMLElement[]
  declare readonly dotTargets: HTMLElement[]
  declare readonly connTargets: HTMLElement[]
  declare readonly labelTargets: HTMLElement[]
  declare readonly miniTarget: HTMLElement
  declare readonly brushTarget: HTMLElement
  declare readonly maskLTarget: HTMLElement
  declare readonly maskRTarget: HTMLElement
  declare readonly minValue: string
  declare readonly maxValue: string
  declare readonly selectedValue: string
  declare readonly typeParamValue: string
  declare readonly basePathValue: string
  declare readonly frameIdValue: string
  declare readonly frameSrcValue: string

  // Tunables.
  private readonly LABEL_GAP_PX = 78 // min horizontal room between labels in one level
  private readonly STEP = 30 // vertical distance between stacked label levels
  private readonly NEAR_GAP = 16 // gap from the line to the closest label level
  private readonly LABEL_H = 26 // label box height (name + date)
  private readonly STAGE_PAD = 12 // breathing room above the top / below the bottom level
  private readonly MAX_LEVEL = 3 // deepest stack level per side; denser clusters
  // keep only their dot (a release marker, no dangling line) — zoom to read them

  private points: Point[] = []
  private byI = new Map<string, Point>()
  private fullMin = 0
  private fullMax = 0
  private dispMin = 0
  private dispMax = 0
  private minSpan = 0
  private selectedTime = 0
  private trackY = 104
  private dragMode: DragMode = null
  private suppressClick = false
  // Brush-drag anchors (captured on pointerdown).
  private dragStartTime = 0
  private dragStartMin = 0
  private dragStartMax = 0
  // Rail pan (wheel / drag) state.
  private panStartX = 0
  private didPan = false
  private wheelSaveTimer = 0
  // Rects cached for the duration of a drag (cleared on pointerup) so move
  // handlers don't force a layout via getBoundingClientRect every event.
  private miniRect: DOMRect | null = null
  private stageRect: DOMRect | null = null
  // Coalesce layout() to one call per frame during drag/wheel.
  private layoutRaf: number | null = null

  private onPointerMove = (e: PointerEvent) => this.handlePointerMove(e)
  private onPointerUp = () => this.handlePointerUp()
  private onMiniDown = (e: PointerEvent) => this.handleMiniDown(e)
  private onStageDown = (e: PointerEvent) => this.handleStageDown(e)
  private onWheel = (e: WheelEvent) => this.handleWheel(e)
  private onStageOver = (e: Event) => this.setHover(e, true)
  private onStageOut = (e: Event) => this.setHover(e, false)
  private onResize = () => this.layout()

  connect() {
    this.fullMin = this.dispMin = new Date(this.minValue).getTime()
    this.fullMax = this.dispMax = new Date(this.maxValue).getTime()
    this.selectedTime = new Date(this.selectedValue).getTime()
    // Smallest zoom window: ~1% of the span, floored at 10 days, so the grips
    // can't cross and a tight cluster still has room to breathe.
    this.minSpan = Math.max(10 * DAY, (this.fullMax - this.fullMin) * 0.01)
    this.collectPoints()
    // Restore the brush window saved before the last frame reload so picking a
    // date doesn't snap the zoom back to full range.
    this.restoreWindow()
    this.layout()
    // Enable the eased height/position transitions only AFTER the first
    // (instant) layout, so the initial paint doesn't slide into place.
    requestAnimationFrame(() => this.stageTarget.classList.add("tl-anim"))

    this.miniTarget.addEventListener("pointerdown", this.onMiniDown)
    this.stageTarget.addEventListener("pointerdown", this.onStageDown)
    window.addEventListener("pointermove", this.onPointerMove)
    window.addEventListener("pointerup", this.onPointerUp)
    this.stageTarget.addEventListener("wheel", this.onWheel, { passive: false })
    this.stageTarget.addEventListener("mouseover", this.onStageOver)
    this.stageTarget.addEventListener("mouseout", this.onStageOut)
    window.addEventListener("resize", this.onResize)
  }

  disconnect() {
    window.clearTimeout(this.wheelSaveTimer)
    if (this.layoutRaf !== null) {
      cancelAnimationFrame(this.layoutRaf)
      this.layoutRaf = null
    }
    this.miniTarget.removeEventListener("pointerdown", this.onMiniDown)
    this.stageTarget.removeEventListener("pointerdown", this.onStageDown)
    window.removeEventListener("pointermove", this.onPointerMove)
    window.removeEventListener("pointerup", this.onPointerUp)
    this.stageTarget.removeEventListener("wheel", this.onWheel)
    this.stageTarget.removeEventListener("mouseover", this.onStageOver)
    this.stageTarget.removeEventListener("mouseout", this.onStageOut)
    window.removeEventListener("resize", this.onResize)
  }

  private collectPoints() {
    const byIndex = (els: HTMLElement[]) => {
      const map = new Map<string, HTMLElement>()
      els.forEach((el) => map.set(el.dataset.tlI || "", el))
      return map
    }
    const conns = byIndex(this.connTargets)
    const labels = byIndex(this.labelTargets)

    this.points = this.dotTargets.map((dot) => {
      const i = dot.dataset.tlI || ""
      const iso = dot.dataset.tlDate || ""
      const label = labels.get(i)!
      return {
        idx: 0,
        time: new Date(iso).getTime(),
        iso,
        model: label?.dataset.tlModel || "",
        date: label?.dataset.tlDateLabel || "",
        dot,
        conn: conns.get(i)!,
        label
      }
    })
    this.points.sort((a, b) => a.time - b.time)
    this.points.forEach((p, i) => (p.idx = i))
    this.byI = new Map(this.points.map((p) => [p.dot.dataset.tlI || "", p]))
  }

  // ── Geometry ────────────────────────────────────────────────────────────────

  private ratio(time: number): number {
    const span = this.dispMax - this.dispMin || 1
    return (time - this.dispMin) / span
  }

  private fullRatio(time: number): number {
    const span = this.fullMax - this.fullMin || 1
    return (time - this.fullMin) / span
  }

  private clamp01(n: number): number {
    return Math.max(0, Math.min(1, n))
  }

  private isSelected(p: Point): boolean {
    return Math.abs(p.time - this.selectedTime) < DAY / 2
  }

  // Greedy stacking: walk visible labels left→right; each takes its fixed side
  // (by chronological parity) and the lowest level on that side that doesn't
  // overlap the previous label there. Stacking is identical whether or not a
  // point is selected — selection only restyles a label in place, so the layout
  // never reflows when you pick/unpick a model. The one exception: the selected
  // point is always slotted (forced to the deepest level if a dense cluster
  // would otherwise drop it to a dot), so its badge is never missing.
  private assignLevels(
    visible: Point[],
    xOf: (p: Point) => number
  ): { slots: Map<Point, Slot>; maxAbove: number; maxBelow: number } {
    const lastAbove: number[] = []
    const lastBelow: number[] = []
    const slots = new Map<Point, Slot>()
    let maxAbove = -1
    let maxBelow = -1

    for (const p of visible) {
      const x = xOf(p)
      const above = p.idx % 2 === 0
      const lanes = above ? lastAbove : lastBelow
      let chosen = -1
      for (let level = 0; level <= this.MAX_LEVEL; level++) {
        const last = lanes[level]
        if (last === undefined || x - last >= this.LABEL_GAP_PX) {
          chosen = level
          break
        }
      }
      if (chosen < 0) {
        if (!this.isSelected(p)) continue // too dense even stacked — dot only
        chosen = this.MAX_LEVEL // the selected badge always shows
      }
      lanes[chosen] = x
      slots.set(p, { above, level: chosen })
      if (above) maxAbove = Math.max(maxAbove, chosen)
      else maxBelow = Math.max(maxBelow, chosen)
    }
    return { slots, maxAbove, maxBelow }
  }

  // ── Layout ────────────────────────────────────────────────────────────────

  private layout() {
    this.layoutMinimap()

    const width = this.stageTarget.clientWidth || 1
    const edgePx = this.LABEL_GAP_PX / 2

    // Ticks (year numbers, year/month marks): position + cull out-of-window.
    this.tickTargets.forEach((t) => {
      const time = new Date(t.dataset.tlDate || "").getTime()
      const r = this.ratio(time)
      const inWin = r >= -0.001 && r <= 1.001
      t.style.left = `${this.clamp01(r) * 100}%`
      t.style.opacity = inWin ? "" : "0"
    })

    // Dots + leaders follow their point's x; vertical placement comes later.
    for (const p of this.points) {
      const r = this.ratio(p.time)
      const inWin = r >= -0.02 && r <= 1.02
      const leftPct = `${this.clamp01(r) * 100}%`
      p.dot.style.left = leftPct
      p.conn.style.left = leftPct
      p.label.style.left = leftPct
      p.dot.classList.toggle("tl-dot--on", this.isSelected(p))
      this.show(p.dot, inWin)
      // Hidden by default; the stacking pass re-shows the in-window ones.
      this.show(p.conn, false)
      this.show(p.label, false)
    }

    // Position the lime "you are here" marker on the line.
    const selR = this.clamp01(this.ratio(this.selectedTime))
    this.handleTarget.style.left = `${selR * 100}%`

    // Stack EVERY visible label (including the selected one) the same way, so
    // selection never reflows the layout — it only restyles a label as a badge.
    const visible = this.points
      .filter((p) => {
        const r = this.ratio(p.time)
        return r >= -0.001 && r <= 1.001
      })
      .sort((a, b) => this.ratio(a.time) - this.ratio(b.time))

    const { slots, maxAbove, maxBelow } = this.assignLevels(visible, (p) => this.ratio(p.time) * width)

    // Size the stage to fit the levels actually used, and centre the line.
    const extentAbove = this.NEAR_GAP + Math.max(maxAbove, 0) * this.STEP + this.LABEL_H
    const extentBelow = this.NEAR_GAP + Math.max(maxBelow, 0) * this.STEP + this.LABEL_H
    this.trackY = this.STAGE_PAD + extentAbove
    this.stageTarget.style.height = `${this.STAGE_PAD + extentAbove + extentBelow + this.STAGE_PAD}px`
    this.stageTarget.style.setProperty("--tl-track", `${this.trackY}px`)

    // Place each stacked label + its leader, edge-anchored so nothing clips.
    // The selected point's title carries the black-badge styling in place.
    for (const [p, slot] of slots) {
      const sel = this.isSelected(p)
      const leftPx = this.ratio(p.time) * width
      if (leftPx < edgePx) p.label.style.transform = "translateX(0)"
      else if (leftPx > width - edgePx) p.label.style.transform = "translateX(-100%)"
      else p.label.style.transform = "translateX(-50%)"
      this.placeAnno(p, slot)
      p.label.classList.toggle("is-selected", sel)
      p.conn.classList.toggle("is-selected", sel)
      this.show(p.conn, true)
      this.show(p.label, true)
    }
  }

  // Coalesce layout() to a single call per animation frame: a burst of
  // pointermove/wheel events updates dispMin/dispMax synchronously but only
  // repaints once per frame.
  private scheduleLayout() {
    if (this.layoutRaf !== null) return
    this.layoutRaf = requestAnimationFrame(() => {
      this.layoutRaf = null
      this.layout()
    })
  }

  // Run any pending coalesced layout right now (used on drag end so the final
  // position is committed synchronously).
  private flushLayout() {
    if (this.layoutRaf === null) return
    cancelAnimationFrame(this.layoutRaf)
    this.layoutRaf = null
    this.layout()
  }

  // Toggle the eased height/position transitions off while dragging so the
  // window tracks the pointer instantly, then back on at pointerup.
  private setAnim(on: boolean) {
    this.stageTarget.classList.toggle("tl-anim", on)
  }

  private placeAnno(p: Point, slot: Slot) {
    const nearEdge = this.NEAR_GAP + slot.level * this.STEP
    if (slot.above) {
      const connTop = this.trackY - nearEdge
      p.conn.style.top = `${connTop}px`
      p.conn.style.height = `${nearEdge}px`
      p.label.style.top = `${connTop - this.LABEL_H}px`
    } else {
      p.conn.style.top = `${this.trackY}px`
      p.conn.style.height = `${nearEdge}px`
      p.label.style.top = `${this.trackY + nearEdge}px`
    }
  }

  private layoutMinimap() {
    const lPct = this.clamp01(this.fullRatio(this.dispMin)) * 100
    const rPct = this.clamp01(this.fullRatio(this.dispMax)) * 100
    this.brushTarget.style.left = `${lPct}%`
    this.brushTarget.style.width = `${rPct - lPct}%`
    this.maskLTarget.style.left = "0"
    this.maskLTarget.style.width = `${lPct}%`
    this.maskRTarget.style.left = `${rPct}%`
    this.maskRTarget.style.width = `${100 - rPct}%`
  }

  private show(el: HTMLElement, visible: boolean) {
    el.style.opacity = visible ? "" : "0"
    el.style.pointerEvents = visible ? "" : "none"
  }

  // ── Click-to-select (main rail) ──────────────────────────────────────────
  // Selection only happens on the real targets — a release dot or its name,
  // both of which show a pointer cursor — so the affordance always matches
  // what's clickable. Bare track does nothing.

  jumpTo(e: Event) {
    e.stopPropagation()
    if (this.suppressClick) return // a pan just ended on this target — ignore
    const iso = (e.currentTarget as HTMLElement).dataset.markerDate
    if (iso) {
      try { window.umami?.track("arena-history-jump", { date: iso, type: this.typeParamValue || null }) } catch {}
      this.commit(iso)
    }
  }

  // ── Minimap brush drag (zoom / pan) ───────────────────────────────────────

  private handleMiniDown(e: PointerEvent) {
    const mode = (e.target as HTMLElement).closest("[data-mini-drag]")?.getAttribute("data-mini-drag")
    if (mode !== "left" && mode !== "right" && mode !== "body") return
    this.dragMode = mode
    // Cache the minimap rect for the whole drag so per-move reads don't reflow.
    this.miniRect = this.miniTarget.getBoundingClientRect()
    this.dragStartTime = this.timeFromClientXMini(e.clientX)
    this.dragStartMin = this.dispMin
    this.dragStartMax = this.dispMax
    this.setAnim(false)
    this.miniTarget.setPointerCapture?.(e.pointerId)
    e.preventDefault()
  }

  private timeFromClientXMini(clientX: number): number {
    const rect = this.miniRect ?? this.miniTarget.getBoundingClientRect()
    const r = this.clamp01((clientX - rect.left) / rect.width)
    return this.fullMin + r * (this.fullMax - this.fullMin)
  }

  private handlePointerMove(e: PointerEvent) {
    if (!this.dragMode) return

    // Rail drag-to-pan (covers touch sliding and mouse drag on the timeline).
    if (this.dragMode === "pan") {
      const dx = e.clientX - this.panStartX
      if (Math.abs(dx) > 5) this.didPan = true
      const width = (this.stageRect ?? this.stageTarget.getBoundingClientRect()).width || 1
      const span = this.dragStartMax - this.dragStartMin
      // Drag right → reveal earlier dates (window moves left), like a map.
      let lo = this.dragStartMin - (dx / width) * span
      lo = Math.max(this.fullMin, Math.min(lo, this.fullMax - span))
      this.dispMin = lo
      this.dispMax = lo + span
      this.scheduleLayout()
      return
    }

    const t = this.timeFromClientXMini(e.clientX)
    if (this.dragMode === "left") {
      this.dispMin = Math.max(this.fullMin, Math.min(t, this.dispMax - this.minSpan))
    } else if (this.dragMode === "right") {
      this.dispMax = Math.min(this.fullMax, Math.max(t, this.dispMin + this.minSpan))
    } else {
      const width = this.dragStartMax - this.dragStartMin
      let lo = this.dragStartMin + (t - this.dragStartTime)
      lo = Math.max(this.fullMin, Math.min(lo, this.fullMax - width))
      this.dispMin = lo
      this.dispMax = lo + width
    }
    this.scheduleLayout()
  }

  private handlePointerUp() {
    if (!this.dragMode) return
    const mode = this.dragMode
    this.dragMode = null
    // End-of-drag: flush any pending layout, drop cached rects, restore anim.
    this.flushLayout()
    this.miniRect = null
    this.stageRect = null
    this.setAnim(true)
    if (mode === "pan") {
      // Only swallow the trailing click (and persist) if we actually panned;
      // a press-without-drag stays a select.
      if (this.didPan) {
        this.suppressClick = true
        window.setTimeout(() => (this.suppressClick = false), 0)
        this.saveWindow()
      }
      return
    }
    // Brush moved: persist the window so it survives the next frame reload.
    this.saveWindow()
  }

  // ── Rail pan: drag + wheel ────────────────────────────────────────────────

  private isFullRange(): boolean {
    return this.dispMax - this.dispMin >= this.fullMax - this.fullMin
  }

  private handleStageDown(e: PointerEvent) {
    // Nothing to pan at full range — let the press become a click (select).
    if (this.isFullRange()) return
    this.dragMode = "pan"
    this.panStartX = e.clientX
    this.dragStartMin = this.dispMin
    this.dragStartMax = this.dispMax
    this.didPan = false
    // Cache the stage rect for the whole pan so per-move reads don't reflow.
    this.stageRect = this.stageTarget.getBoundingClientRect()
    this.setAnim(false)
  }

  // Horizontal wheel / trackpad / Magic Mouse swipe pans the window. A vertical
  // wheel is left alone so it scrolls the page as usual. At an edge we also let
  // the page scroll rather than trapping it.
  private handleWheel(e: WheelEvent) {
    if (this.isFullRange()) return
    // Horizontal intent only — ignore vertical-dominant scrolling.
    if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return
    const delta = e.deltaX
    if (delta === 0) return
    const width = this.stageTarget.getBoundingClientRect().width || 1
    const span = this.dispMax - this.dispMin
    let lo = this.dispMin + (delta / width) * span
    lo = Math.max(this.fullMin, Math.min(lo, this.fullMax - span))
    if (lo === this.dispMin) return // at the edge — don't trap the page scroll
    this.dispMin = lo
    this.dispMax = lo + span
    this.scheduleLayout()
    e.preventDefault()
    window.clearTimeout(this.wheelSaveTimer)
    this.wheelSaveTimer = window.setTimeout(() => this.saveWindow(), 400)
  }

  // ── Hover highlight ───────────────────────────────────────────────────────
  // Hovering ANY part of a release — its dot, leader line, or name — emphasises
  // the whole trio. Dots are tiny and overlap in clusters, so the spread-out
  // label is the reliable hover target.

  private setHover(e: Event, on: boolean) {
    const el = (e.target as HTMLElement).closest?.(".tl-dot, .tl-label, .tl-conn") as HTMLElement | null
    if (!el) return
    const p = this.byI.get(el.dataset.tlI || "")
    if (!p) return
    p.dot.classList.toggle("is-hover", on)
    p.conn.classList.toggle("is-hover", on)
    p.label.classList.toggle("is-hover", on)
  }

  // ── Window persistence ───────────────────────────────────────────────────
  // The brush window is a per-mode view preference, so it's keyed by type and
  // kept in sessionStorage: it survives the turbo-frame reload that a date pick
  // triggers (and a full page refresh) but doesn't linger past the tab.

  private storageKey(): string {
    return `arena-history-zoom:${this.typeParamValue || "all"}`
  }

  private restoreWindow() {
    try {
      const raw = window.sessionStorage.getItem(this.storageKey())
      if (!raw) return
      const saved = JSON.parse(raw) as { min?: number; max?: number }
      if (typeof saved.min !== "number" || typeof saved.max !== "number") return
      const min = Math.max(this.fullMin, Math.min(saved.min, this.fullMax))
      const max = Math.max(this.fullMin, Math.min(saved.max, this.fullMax))
      if (max - min < this.minSpan) return // stale/degenerate → keep full range
      this.dispMin = min
      this.dispMax = max
    } catch {
      // sessionStorage may be unavailable (private mode, blocked) — falling back
      // to the full-range window is the correct degradation, not an error.
    }
  }

  private saveWindow() {
    try {
      const isFull = this.dispMin <= this.fullMin && this.dispMax >= this.fullMax
      if (isFull) {
        window.sessionStorage.removeItem(this.storageKey()) // no zoom to remember
        return
      }
      window.sessionStorage.setItem(this.storageKey(), JSON.stringify({ min: this.dispMin, max: this.dispMax }))
    } catch {
      // Best-effort persistence; ignore storage failures.
    }
  }

  // ── Commit ────────────────────────────────────────────────────────────────

  private commit(iso: string) {
    const url = new URL(this.basePathValue, window.location.origin)
    url.searchParams.set("date", iso)
    if (this.typeParamValue) url.searchParams.set("type", this.typeParamValue)
    else url.searchParams.delete("type")
    if (window.location.search.includes("preview=1")) url.searchParams.set("preview", "1")
    const finalUrl = url.pathname + (url.search || "")

    // Frame mode: hot-swap the scrubber frame, then mirror the date/type into
    // the page URL so deep-links and back/forward work without a full nav.
    if (this.frameIdValue && this.frameSrcValue) {
      const frame = document.getElementById(this.frameIdValue) as (HTMLElement & { src?: string }) | null
      if (frame) {
        const frameUrl = new URL(this.frameSrcValue, window.location.origin)
        frameUrl.searchParams.set("date", iso)
        if (this.typeParamValue) frameUrl.searchParams.set("type", this.typeParamValue)
        else frameUrl.searchParams.delete("type")
        if (window.location.search.includes("preview=1")) frameUrl.searchParams.set("preview", "1")
        frame.setAttribute("src", frameUrl.pathname + (frameUrl.search || ""))
        window.history.replaceState({}, "", finalUrl)
        return
      }
    }

    const turbo = (window as unknown as { Turbo?: { visit: (u: string, o?: { action?: string }) => void } }).Turbo
    if (turbo) turbo.visit(finalUrl, { action: "replace" })
    else window.location.href = finalUrl
  }
}
